import { Component, createRef } from 'react'
import {
  autoUpdate,
  computePosition,
  flip,
  FloatingPortal,
  offset,
  shift,
} from '@floating-ui/react'
import PropTypes from 'prop-types'
import $ from 'jquery'

import Icon from '../../Icons'

class SelectField extends Component {
  constructor(props) {
    super(props)
    this.state = {
      dropdownOpen: false,
      dropdownStyle: null,
    }
    this.triggerRef = createRef()
    this.dropdownRef = createRef()
    this.cleanupAutoUpdate = null
    this.clickHandler = this.clickHandler.bind(this)
    this.toggleDropdown = this.toggleDropdown.bind(this)
    this.selectOption = this.selectOption.bind(this)
    this.updateFloatingPosition = this.updateFloatingPosition.bind(this)
  }

  componentDidMount() {
    $(document.body).on('click', this.clickHandler)
  }

  componentDidUpdate(prevProps, prevState) {
    if (!this.props.floatDropdown) return
    if (this.state.dropdownOpen && !prevState.dropdownOpen) {
      this.cleanupAutoUpdate = autoUpdate(
        this.triggerRef.current,
        this.dropdownRef.current,
        this.updateFloatingPosition,
      )
    } else if (!this.state.dropdownOpen && prevState.dropdownOpen) {
      this.stopAutoUpdate()
    }
  }

  componentWillUnmount() {
    $(document.body).off('click', this.clickHandler)
    this.stopAutoUpdate()
  }

  stopAutoUpdate() {
    if (this.cleanupAutoUpdate) {
      this.cleanupAutoUpdate()
      this.cleanupAutoUpdate = null
    }
  }

  updateFloatingPosition() {
    if (!this.triggerRef.current || !this.dropdownRef.current) return
    computePosition(this.triggerRef.current, this.dropdownRef.current, {
      placement: 'bottom-start',
      middleware: [offset(4), flip(), shift({ padding: 8 })],
    }).then(({ x, y }) => {
      this.setState({
        dropdownStyle: {
          position: 'fixed',
          top: y,
          left: x,
          width: this.triggerRef.current.getBoundingClientRect().width,
          display: 'flex',
          zIndex: 1000,
        },
      })
    })
  }

  clickHandler(e) {
    const clickedTrigger = $(e.target).closest('.field-select .control').length > 0
    const clickedFloatingDropdown =
      this.props.floatDropdown &&
      this.dropdownRef.current &&
      this.dropdownRef.current.contains(e.target)
    if (!clickedTrigger && !clickedFloatingDropdown && this.state.dropdownOpen) {
      this.closeDropdown()
    }
  }

  closeDropdown() {
    this.setState({ dropdownOpen: false })
    if (this.props.onBlur) {
      this.props.onBlur()
    }
  }

  toggleDropdown(e) {
    e.preventDefault()
    if (this.state.dropdownOpen) {
      this.closeDropdown()
      return
    }
    if (this.props.floatDropdown && this.triggerRef.current) {
      const rect = this.triggerRef.current.getBoundingClientRect()
      this.setState({
        dropdownOpen: true,
        dropdownStyle: {
          position: 'fixed',
          top: rect.bottom + 4,
          left: rect.left,
          width: rect.width,
          display: 'flex',
          zIndex: 1000,
        },
      })
      return
    }
    this.setState({ dropdownOpen: true })
  }

  selectOption(e, value) {
    e.preventDefault()
    this.closeDropdown()
    this.props.onChange({ target: { name: this.props.name, value } })
  }

  renderOptions() {
    const { options, value, withIcon, renderRightElement } = this.props
    return (
      options.length > 0 &&
      options
        .filter((opt) => opt.value !== value)
        .map((option) => (
          <div
            className={`field-select__dropdown_wrapper ${option.disabled ? 'disabled' : ''} ${
              renderRightElement ? 'with-right-element' : ''
            }`}
            data-value={option.value}
            onClick={(e) => !option.disabled && this.selectOption(e, option.value)}
            key={`${option.label}-${option.value}`}
          >
            {withIcon && (
              <div className="field-select__icon-wrapper">
                <Icon
                  className="field-select__icon"
                  viewBox={'0 0 22 22'}
                  fill="#f2ecec"
                  svgProps={{
                    stroke: 'rgba(141, 140, 140, 1)',
                    strokeWidth: '1.5',
                    strokeLinecap: 'round',
                    strokeLinejoin: 'round',
                  }}
                  icon={option.value}
                />
              </div>
            )}
            <span>{option.label}</span>
            {renderRightElement && renderRightElement(option)}
          </div>
        ))
    )
  }

  render() {
    const {
      options,
      value,
      placeholder,
      label,
      additionalClass,
      icon,
      disabled,
      isError,
      style,
      placeholderStyle,
      selectStyle,
      extraInputClass = '',
      floatDropdown,
    } = this.props
    const selectedValue = value ? options.find((el) => el.value === value) : false
    const { dropdownOpen, dropdownStyle } = this.state
    return (
      <div
        className={`field field-select ${dropdownOpen ? 'is-open' : ''} ${additionalClass || ''} ${
          disabled ? 'field--disabled' : ''
        } ${isError ? 'is-danger' : ''}`}
        style={style}
      >
        {label && <label className="label">{label}</label>}
        <div className="control">
          <div className="select" style={selectStyle} ref={floatDropdown ? this.triggerRef : null}>
            <div
              className={`field-select__input input ${extraInputClass}`}
              onClick={!disabled ? this.toggleDropdown : undefined}
              style={selectStyle}
            >
              {icon && <Icon icon={icon} />}
              {selectedValue ? (
                <span>{selectedValue.label}</span>
              ) : (
                <span style={placeholderStyle}>{placeholder || 'Select option...'}</span>
              )}
            </div>
            {!floatDropdown && (
              <div className={'field-select__dropdown'}>{this.renderOptions()}</div>
            )}
          </div>
        </div>
        {floatDropdown && dropdownOpen && (
          <FloatingPortal>
            <div className="field-select">
              <div
                className="field-select__dropdown field-select__dropdown--floating"
                style={dropdownStyle}
                ref={this.dropdownRef}
              >
                {this.renderOptions()}
              </div>
            </div>
          </FloatingPortal>
        )}
      </div>
    )
  }
}

SelectField.propTypes = {
  name: PropTypes.string.isRequired,
  // defaultValue must be a valid option from options array
  value: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  options: PropTypes.arrayOf(
    PropTypes.shape({
      value: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
      label: PropTypes.oneOfType([PropTypes.string, PropTypes.number, PropTypes.node]),
    }),
  ).isRequired,
  placeholder: PropTypes.string,
  label: PropTypes.string,
  additionalClass: PropTypes.string,
  icon: PropTypes.string,
  disabled: PropTypes.bool,
  onChange: PropTypes.func,
  isError: PropTypes.oneOfType([PropTypes.string, PropTypes.bool]),
  style: PropTypes.object,
  selectStyle: PropTypes.object,
  placeholderStyle: PropTypes.object,
  withIcon: PropTypes.bool,
  onBlur: PropTypes.func,
  renderRightElement: PropTypes.func,
  extraInputClass: PropTypes.string,
  floatDropdown: PropTypes.bool,
}

export default SelectField
